/**
 * 路由器：页面栈（NavState）与浏览器 History 的同步层。
 *
 * 设计要点（下游 5b/5c 依赖这些语义）：
 * - 单一真相源：entries + cursor 完整镜像浏览器历史，栈 = entries[0..cursor]。
 *   H5 页面栈和 PC 双栏是同一 NavState 的两种投影（渲染归 5b），
 *   跨断点缩放切换天然不丢会话级状态（修复 legacy §4.5）。
 * - 返回（浏览器返回键 / 右滑手势 / standalone 应用内返回按钮）统一走
 *   history.back()，出栈在 popstate 回调里完成——应用栈与浏览器历史永不分叉。
 * - 每个历史条目的 state 存 {idx, path}：popstate 时 idx 定位 cursor
 *   （支持一次跳多级），path 供前进恢复；校验失败（null/外部 state）按当前 URL 重建。
 * - init()：当前条目已带本应用 state 且与合成栈一致（= 刷新）→ 只 replace 不重播
 *   push 链，避免历史里堆重复条目；否则（首次进入/深链）replace 根 + 逐个 push
 *   合成栈，让返回键逐级出栈而不是退出站点。
 * - 栈底 back() 是 no-op：canGoBack() 为 false 时 standalone 返回按钮不渲染。
 */
import { invariant } from '~/util/invariant';
import {
  isSameRoute,
  parseRoute,
  routePath,
  stackForPath,
  type AppRoute,
} from './routes';

export interface NavState {
  /** 栈底恒为根页面；栈顶 = 当前页。 */
  readonly stack: readonly AppRoute[];
}

/** 可注入的最小 History 形状（测试用 FakeHistory，运行时是 window.history）。 */
export interface HistoryLike {
  readonly state: unknown;
  pushState(state: unknown, url: string): void;
  replaceState(state: unknown, url: string): void;
  back(): void;
  /** history.go(delta)：popWhile 一次跳多级用（popstate 回调里完成出栈） */
  go(delta: number): void;
}

export interface RouterDeps {
  history: HistoryLike;
  /** 当前 location.pathname。 */
  getPath(): string;
  /** 订阅 popstate，回调参数 = event.state；返回取消订阅函数。 */
  onPopState(listener: (state: unknown) => void): () => void;
  /** 栈变化回调（5b 接线到 globalStore.set({ nav })）。 */
  onChange(nav: NavState): void;
}

export interface Router {
  init(): void;
  push(route: AppRoute): void;
  replace(route: AppRoute): void;
  /** 统一返回入口；栈底时 no-op。 */
  back(): void;
  /**
   * 从栈顶起连续弹出满足 pred 的条目（history.go(-n) 一次跳多级，出栈在
   * popstate 回调完成；handlePopState 本就支持多级）。栈底恒保留；栈顶不满足
   * 或已在栈底时 no-op。14.21b 首用：删好友/拉黑后把详情页+其下方该好友的
   * 聊天页一次退干净（被购销的会话页不能留在历史里）。
   */
  popWhile(pred: (route: AppRoute) => boolean): void;
  canGoBack(): boolean;
  getNav(): NavState;
  dispose(): void;
}

interface EntryState {
  readonly lhimNavIdx: number;
  readonly lhimNavPath: string;
}

function entryState(idx: number, route: AppRoute): EntryState {
  return { lhimNavIdx: idx, lhimNavPath: routePath(route) };
}

/** history.state → {idx, route}；形状或 path 不合法返回 undefined。 */
function parseEntryState(value: unknown): { idx: number; route: AppRoute } | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const v = value as Record<string, unknown>;
  if (typeof v['lhimNavIdx'] !== 'number' || typeof v['lhimNavPath'] !== 'string') {
    return undefined;
  }
  const idx = v['lhimNavIdx'];
  if (!Number.isInteger(idx) || idx < 0) return undefined;
  const route = parseRoute(v['lhimNavPath']);
  return route ? { idx, route } : undefined;
}

export function createRouter(deps: RouterDeps): Router {
  let entries: AppRoute[] = [];
  let cursor = -1;
  let unsubscribe: (() => void) | undefined;

  const getNav = (): NavState => ({ stack: entries.slice(0, cursor + 1) });
  const emit = (): void => deps.onChange(getNav());

  /** 以当前 URL 的合成栈整体重建，并把当前历史条目锚定成本应用 state。 */
  function rebuildFromLocation(): void {
    entries = stackForPath(deps.getPath());
    cursor = entries.length - 1;
    const top = entries[cursor];
    invariant(top, 'stackForPath 返回空栈');
    deps.history.replaceState(entryState(cursor, top), routePath(top));
  }

  function handlePopState(state: unknown): void {
    const parsed = parseEntryState(state);
    if (parsed && parsed.idx < entries.length) {
      const known = entries[parsed.idx];
      invariant(known, 'cursor 越界');
      if (isSameRoute(known, parsed.route)) {
        // 同一条链内的后退/前进（含一次跳多级）
        cursor = parsed.idx;
        emit();
        return;
      }
    }
    // 外部 state / 旧会话残留条目 / 手改 URL：按当前 URL 重建
    rebuildFromLocation();
    emit();
  }

  return {
    init() {
      const synthesized = stackForPath(deps.getPath());
      const existing = parseEntryState(deps.history.state);
      entries = synthesized;
      cursor = synthesized.length - 1;
      const top = entries[cursor];
      invariant(top, 'stackForPath 返回空栈');
      if (existing && existing.idx === cursor && isSameRoute(existing.route, top)) {
        // 刷新：历史链上一轮已铺好，只刷新当前条目，不重复 push
        deps.history.replaceState(entryState(cursor, top), routePath(top));
      } else {
        deps.history.replaceState(entryState(0, entries[0]!), routePath(entries[0]!));
        for (let i = 1; i < entries.length; i++) {
          deps.history.pushState(entryState(i, entries[i]!), routePath(entries[i]!));
        }
      }
      unsubscribe = deps.onPopState(handlePopState);
      emit();
    },

    push(route) {
      // 丢弃前进分支，与 pushState 截断行为保持一致
      entries = entries.slice(0, cursor + 1);
      entries.push(route);
      cursor++;
      deps.history.pushState(entryState(cursor, route), routePath(route));
      emit();
    },

    replace(route) {
      invariant(cursor >= 0, 'replace 前必须 init');
      entries[cursor] = route;
      deps.history.replaceState(entryState(cursor, route), routePath(route));
      emit();
    },

    back() {
      if (cursor > 0) {
        deps.history.back(); // 出栈在 popstate 回调里完成
      }
    },

    popWhile(pred) {
      let n = 0;
      while (cursor - n > 0 && pred(entries[cursor - n]!)) n++;
      if (n > 0) deps.history.go(-n);
    },

    canGoBack: () => cursor > 0,
    getNav,

    dispose() {
      unsubscribe?.();
      unsubscribe = undefined;
    },
  };
}
